import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { isAITransformation } from '../../utils/transformations';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { PresetCard } from '../jobs/PresetCard';
import { 
  Sparkles, 
  Move, 
  FileImage, 
  Zap, 
  Palette,
  Plus,
  X,
  Loader2,
  Calculator,
  CheckCircle,
  Settings,
  Upload,
  Clock,
  DollarSign,
  Info,
  Wand2,
  Eye,
  Tag,
  Brain,
  ArrowDown,
  ChevronUp,
  ChevronDown,
  AlertTriangle,
  ArrowUp,
  Users,
  Store,
  ArrowUpCircle,
  Sun,
  Replace,
  Shirt,
  Home
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Transformation, TransformationPreset, jobService } from '../../services/job';
import { Product } from '../../services/product';
import { useUnifiedSubscription } from '../../contexts/SubscriptionContext';
import { formatCredits, formatNumber, safeSubtract, safeAdd, toSafeNumber } from '../../utils/number';
import { PresetInputDialog } from '../jobs/PresetInputDialog';

interface ProductProcessingModalProps {
  product: Product;
  isOpen: boolean;
  onClose: () => void;
  onJobCreated: (jobId: string, isAsync?: boolean) => void;
}

export function ProductProcessingModal({
  product,
  isOpen,
  onClose,
  onJobCreated
}: ProductProcessingModalProps) {
  const navigate = useNavigate();
  const {
    usageDetails,
    checkFeatureAccess,
    openSubscriptionModal,
    isLoading: subscriptionLoading,
    subscription
  } = useUnifiedSubscription();

  const [transformations, setTransformations] = useState<Transformation[]>([]);
  const [presets, setPresets] = useState<Record<string, TransformationPreset>>({});
  const [isCreatingJobs, setIsCreatingJobs] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customTransformation, setCustomTransformation] = useState<Partial<Transformation>>({});
  const [processingInfo, setProcessingInfo] = useState<any>(null);
  const [isAnalyzingTransformations, setIsAnalyzingTransformations] = useState(false);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [creditCheckFailed, setCreditCheckFailed] = useState(false);
  const [showPresetInputDialog, setShowPresetInputDialog] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState<TransformationPreset | null>(null);

  // Get all available images from the product
  const getAllImages = React.useCallback((): string[] => {
    const images: string[] = [];

    if (product.main_image_url) {
      images.push(product.main_image_url);
    }

    if (product.gallery_images && Array.isArray(product.gallery_images)) {
      product.gallery_images.forEach((img: any) => {
        // Handle both string URLs and objects with url property
        if (typeof img === 'string') {
          if (!images.includes(img)) {
            images.push(img);
          }
        } else if (img && img.url && !images.includes(img.url)) {
          images.push(img.url);
        }
      });
    }

    return images;
  }, [product]);

  // Get image metadata for a specific URL
  const getImageMetadata = React.useCallback((imageUrl: string): any => {
    if (!product.gallery_images || !Array.isArray(product.gallery_images)) {
      return null;
    }

    for (const img of product.gallery_images) {
      if (typeof img === 'object' && img.url === imageUrl) {
        return img;
      }
    }

    return null;
  }, [product]);

  // Format Magento image role for display
  const formatImageRole = (role: string): string => {
    const roleMap: Record<string, string> = {
      'image': 'Main',
      'small_image': 'Small',
      'thumbnail': 'Thumb',
      'swatch_image': 'Swatch',
      'base': 'Base'
    };
    return roleMap[role] || role;
  };

  // Calculate image count based on selected images
  const imageCount = React.useMemo(() => {
    return selectedImages.length;
  }, [selectedImages]);

  // Initialize selected images when modal opens
  useEffect(() => {
    if (isOpen) {
      const allImages = getAllImages();
      setSelectedImages(allImages); // Select all images by default
    }
  }, [isOpen, getAllImages]);

  useEffect(() => {
    if (isOpen) {
      loadPresets();
    }
  }, [isOpen]);

  // Analyze transformations when they change
  useEffect(() => {
    if (transformations.length > 0) {
      analyzeTransformations();
    } else {
      setProcessingInfo(null);
      setCreditCheckFailed(false);
    }
  }, [transformations, imageCount, usageDetails]);

  const loadPresets = async () => {
    try {
      const response = await jobService.getTransformationPresets();
      if (response.success) {
        setPresets(response.data);
      }
    } catch (error: any) {
      console.error('Failed to load presets:', error);
      setError('Failed to load transformation presets');
    }
  };

  const analyzeTransformations = async () => {
    if (transformations.length === 0) {
      setProcessingInfo(null);
      setCreditCheckFailed(false);
      return;
    }

    setIsAnalyzingTransformations(true);
    try {
      // Calculate processing info locally for now
      const aiFeatures = ['background_removal', 'ai_upscale', 'generative_fill', 'generative_remove', 'generative_replace', 'generative_background_replace', 'auto_tagging', 'object_detection', 'quality_enhancement', 'invisible_mannequin', 'object_placement', 'invisible_mannequin_pro', 'object_placement_pro', 'product_photo_studio', 'jewelry_photo_studio'];
      const bflFeatures = ['invisible_mannequin', 'object_placement']; // BFL API features take longer
      const geminiFeatures = ['invisible_mannequin_pro', 'object_placement_pro', 'product_photo_studio', 'jewelry_photo_studio']; // Gemini API Pro features
      const hasAiFeatures = transformations.some(t => aiFeatures.includes(t.type));
      const hasBflFeatures = transformations.some(t => bflFeatures.includes(t.type));
      const hasGeminiFeatures = transformations.some(t => geminiFeatures.includes(t.type));
      const estimatedCredits = jobService.calculateEstimatedCost(transformations, imageCount);

      // Calculate estimated processing time
      let estimatedTime = '2-5 seconds';
      if (hasGeminiFeatures) {
        estimatedTime = '90-120 seconds'; // Gemini Pro features take longest
      } else if (hasBflFeatures) {
        estimatedTime = '30-60 seconds'; // BFL features take longer
      } else if (hasAiFeatures) {
        estimatedTime = '15-30 seconds';
      }
      
      // Check subscription limits with safe number calculations
      let hasEnoughCredits = true;
      let creditOverage = 0;
      
      if (usageDetails?.credits) {
        const currentCredits = toSafeNumber(usageDetails.credits.current);
        const creditLimit = toSafeNumber(usageDetails.credits.limit);
        const availableCredits = safeSubtract(creditLimit, currentCredits);
        
        if (estimatedCredits > availableCredits) {
          hasEnoughCredits = false;
          creditOverage = safeSubtract(estimatedCredits, availableCredits);
        }
      }
      
      setCreditCheckFailed(!hasEnoughCredits);
      
      setProcessingInfo({
        processing_info: {
          method: hasAiFeatures ? 'upload' : 'fetch',
          storage_required: hasAiFeatures,
          estimated_time: estimatedTime
        },
        stats: {
          credits_per_image: estimatedCredits / imageCount,
          total_credits: estimatedCredits,
          total_cost: estimatedCredits * 0.01, // Assuming $0.01 per credit
          ai_features_count: transformations.filter(t => aiFeatures.includes(t.type)).length
        },
        subscription_check: {
          has_enough_credits: hasEnoughCredits,
          credit_overage: creditOverage,
          current_credits: usageDetails?.credits?.current || 0,
          credit_limit: usageDetails?.credits?.limit || 0,
          available_credits: safeSubtract(
            toSafeNumber(usageDetails?.credits?.limit),
            toSafeNumber(usageDetails?.credits?.current)
          )
        }
      });
    } catch (error) {
      console.error('Failed to analyze transformations:', error);
    } finally {
      setIsAnalyzingTransformations(false);
    }
  };

  const applyPreset = (presetName: string) => {
    const preset = presets[presetName];
    if (preset) {
      // Check if preset requires input
      if (preset.requiresInput && preset.inputFields && preset.inputFields.length > 0) {
        // Pre-populate with product name if this is object_placement or object_placement_pro
        const isObjectPlacement = presetName === 'object_placement' || presetName === 'object_placement_pro';
        const transformationType = preset.transformations[0]?.type;

        if (isObjectPlacement && (transformationType === 'object_placement' || transformationType === 'object_placement_pro')) {
          const presetWithProductName = {
            ...preset,
            transformations: preset.transformations.map(t =>
              (t.type === 'object_placement' || t.type === 'object_placement_pro')
                ? { ...t, product: product.name }
                : t
            )
          };
          setSelectedPreset(presetWithProductName);
        } else {
          setSelectedPreset(preset);
        }
        setShowPresetInputDialog(true);
      } else {
        setTransformations([...preset.transformations]);
        setError(null);
      }
    }
  };

  const handlePresetInputConfirm = (updatedTransformations: Transformation[]) => {
    setTransformations(updatedTransformations);
    setShowPresetInputDialog(false);
    setSelectedPreset(null);
    setError(null);
  };

  const addCustomTransformation = () => {
    // Validation
    if (!customTransformation.type) {
      setError('Please select a transformation type');
      return;
    }

    if (customTransformation.type === 'resize') {
      if (!customTransformation.width && !customTransformation.height) {
        setError('Width or height is required for resize');
        return;
      }
    }

    if (customTransformation.type === 'generative_fill') {
      if (!customTransformation.width || !customTransformation.height) {
        setError('Both width and height are required for generative fill');
        return;
      }
    }

    if (customTransformation.type === 'generative_remove' && !customTransformation.prompt) {
      setError('Prompt is required for generative remove');
      return;
    }

    if (customTransformation.type === 'generative_replace') {
      if (!customTransformation.from || !customTransformation.to) {
        setError('Both "from" and "to" descriptions are required for generative replace');
        return;
      }
    }

    if (customTransformation.type === 'generative_background_replace' && !customTransformation.prompt) {
      setError('Background description is required for generative background replace');
      return;
    }

    if (customTransformation.type === 'ai_upscale' && !customTransformation.scale) {
      setError('Scale factor is required for AI upscale');
      return;
    }

    if (customTransformation.type === 'color_adjustment') {
      const hasAnyAdjustment = ['brightness', 'saturation', 'contrast', 'hue', 'vibrance'].some(
        prop => customTransformation[prop as keyof Transformation] !== undefined
      );
      if (!hasAnyAdjustment) {
        setError('At least one color adjustment parameter is required');
        return;
      }
    }

    setTransformations([...transformations, customTransformation as Transformation]);
    setCustomTransformation({});
    setShowCustomForm(false);
    setError(null);
  };

  const removeTransformation = (index: number) => {
    setTransformations(transformations.filter((_, i) => i !== index));
  };

  const moveTransformation = (fromIndex: number, toIndex: number) => {
    const newTransformations = [...transformations];
    const [movedItem] = newTransformations.splice(fromIndex, 1);
    newTransformations.splice(toIndex, 0, movedItem);
    setTransformations(newTransformations);
  };

  const getOrderPriority = (type: string) => {
    // Centralized transformation order - matches backend and Python service
    const orderPriority = {
      // Step 1: Content modification
      'generative_remove': 1,
      'generative_replace': 2,
      
      // Step 2: Background operations
      'background_removal': 3,
      'generative_background_replace': 4,
      
      // Step 3: Canvas/size operations
      'generative_fill': 5,
      'ai_upscale': 6,
      'resize': 7,
      'crop': 8,
      'rotate': 9,
      
      // Step 4: Visual adjustments
      'quality_enhancement': 10,
      'auto_enhancement': 11,
      'color_adjustment': 12,
      'sharpen': 13,
      'blur': 14,
      'noise_reduction': 15,
      
      // Step 5: Format and output
      'format_conversion': 16,
      
      // Step 6: Analysis
      'auto_tagging': 17,
      'object_detection': 18
    };
    return orderPriority[type as keyof typeof orderPriority] || 99;
  };

  const autoSortTransformations = () => {
    const sortedTransformations = [...transformations].sort((a, b) => {
      const priorityA = getOrderPriority(a.type);
      const priorityB = getOrderPriority(b.type);
      return priorityA - priorityB;
    });

    setTransformations(sortedTransformations);
  };

  const isOptimalOrder = () => {
    for (let i = 0; i < transformations.length - 1; i++) {
      const currentPriority = getOrderPriority(transformations[i].type);
      const nextPriority = getOrderPriority(transformations[i + 1].type);
      if (currentPriority > nextPriority) {
        return false;
      }
    }
    return true;
  };

  // Handle image selection
  const toggleImageSelection = (imageUrl: string) => {
    setSelectedImages(prev => {
      if (prev.includes(imageUrl)) {
        return prev.filter(url => url !== imageUrl);
      } else {
        return [...prev, imageUrl];
      }
    });
  };

  const selectAllImages = () => {
    setSelectedImages(getAllImages());
  };

  const deselectAllImages = () => {
    setSelectedImages([]);
  };

  const createJobs = async () => {
    if (transformations.length === 0) {
      setError('Please add at least one transformation');
      return;
    }

    if (selectedImages.length === 0) {
      setError('Please select at least one image to process');
      return;
    }

    // Check subscription limits before creating jobs
    const estimatedCredits = jobService.calculateEstimatedCost(transformations, imageCount);
    const hasAccess = await checkFeatureAccess('image_processing', estimatedCredits);
    
    if (!hasAccess) {
      // The checkFeatureAccess function will handle opening the appropriate modal
      return;
    }

    setIsCreatingJobs(true);
    setError(null);

    try {
      const response = await jobService.createBatchJobs({
        product_ids: [product.id],
        transformations,
        store_id: product.store_id,
        selected_images: selectedImages
      });

      if (response.success) {
        // Handle both sync (batch_id) and async (job_id) responses
        const identifier = response.data.batch_id || response.data.job_id || 'unknown';
        const isAsync = response.data.async || false;
        
        onJobCreated(identifier, isAsync);
        onClose();

        // Always navigate to the main jobs page
        navigate('/jobs');
      } else {
        console.error('Job creation failed:', response);
        setError(response.message || 'Failed to create job');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to create processing job');
    } finally {
      setIsCreatingJobs(false);
    }
  };

  const renderProcessingMethodBadge = () => {
    if (!processingInfo) return null;

    const isAiProcessing = processingInfo.processing_info?.method === 'upload';
    
    return (
      <Badge 
        variant={isAiProcessing ? 'default' : 'secondary'}
        className={isAiProcessing 
          ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white' 
          : 'bg-gray-100 text-gray-700'
        }
      >
        {isAiProcessing ? (
          <>
            <Sparkles className="w-3 h-3 mr-1" />
            AI Processing
          </>
        ) : (
          <>
            <Zap className="w-3 h-3 mr-1" />
            Basic Processing
          </>
        )}
      </Badge>
    );
  };

  const renderProcessingEstimate = () => {
    if (!processingInfo) return null;

    const stats = processingInfo.stats;
    const subscriptionCheck = processingInfo.subscription_check;

    return (
      <div className="space-y-3">
        {/* Subscription limit warning */}
        {subscriptionCheck && !subscriptionCheck.has_enough_credits && (
          <Alert variant="destructive" className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/20">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <div className="font-medium text-red-800 dark:text-red-200 mb-2">Insufficient Credits</div>
              <div className="text-sm text-red-700 dark:text-red-300 space-y-1">
                <div>Required: <strong>{formatCredits(stats?.total_credits || 0)}</strong></div>
                <div>Available: <strong>{formatCredits(subscriptionCheck.available_credits || 0)}</strong></div>
                <div>Shortage: <strong>{formatCredits(subscriptionCheck.credit_overage || 0)}</strong></div>
              </div>
              <Button 
                size="sm" 
                className="mt-2 bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600"
                onClick={() => openSubscriptionModal('image_processing', 'credits_exceeded')}
              >
                Upgrade Plan
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Processing estimate card */}
        <Card className={`${creditCheckFailed ? 'bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-950/20 dark:to-orange-950/20 border-red-200 dark:border-red-800' : 'bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950/20 dark:to-purple-950/20 border-blue-200 dark:border-blue-800'}`}>
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div className="text-center">
                <div className="font-semibold text-blue-700 dark:text-blue-300">{processingInfo.processing_info?.estimated_time}</div>
                <div className="text-gray-600 dark:text-gray-400 flex items-center justify-center">
                  <Clock className="w-3 h-3 mr-1" />
                  Processing Time
                </div>
              </div>
              <div className="text-center">
                <div className="font-semibold text-purple-700 dark:text-purple-300">{formatNumber(stats?.credits_per_image || 0, 1)}</div>
                <div className="text-gray-600 dark:text-gray-400 flex items-center justify-center">
                  <Zap className="w-3 h-3 mr-1" />
                  Credits per Image
                </div>
              </div>
              <div className="text-center">
                <div className={`font-semibold ${creditCheckFailed ? 'text-red-700 dark:text-red-300' : 'text-green-700 dark:text-green-300'}`}>
                  {formatNumber(stats?.total_credits || 0)} / {formatNumber(subscriptionCheck?.available_credits || 0)}
                </div>
                <div className="text-gray-600 flex items-center justify-center">
                  <Calculator className="w-3 h-3 mr-1" />
                  Credits Required
                </div>
              </div>
              <div className="text-center">
                <div className="font-semibold text-orange-700 dark:text-orange-300">{stats?.ai_features_count || 0}</div>
                <div className="text-gray-600 dark:text-gray-400 flex items-center justify-center">
                  <Wand2 className="w-3 h-3 mr-1" />
                  AI Features
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Subscription usage info */}
        {subscriptionCheck && (
          <Card className="bg-gray-50 dark:bg-gray-950/50 border-gray-200 dark:border-gray-800">
            <CardContent className="p-3">
              <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                <div className="flex justify-between">
                  <span>Current usage:</span>
                  <span className="font-medium">{formatNumber(subscriptionCheck.current_credits)} / {formatNumber(subscriptionCheck.credit_limit)} credits</span>
                </div>
                <div className="flex justify-between">
                  <span>Usage after processing:</span>
                  <span className={`font-medium ${subscriptionCheck.has_enough_credits ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {formatNumber(safeAdd(subscriptionCheck.current_credits, stats?.total_credits || 0))} / {formatNumber(subscriptionCheck.credit_limit)} credits
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  const renderCustomTransformationForm = () => {
    if (!showCustomForm) return null;

    return (
      <div className="p-6 space-y-6 rounded-lg border-2 border-dashed border-primary/20 dark:border-primary/40 bg-accent/30 dark:bg-accent/10">
        <h4 className="flex gap-2 items-center text-lg font-semibold">
          <Plus className="w-5 h-5 text-primary" />
          Add Custom Transformation
        </h4>
        
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div className="space-y-2">
            <label className="text-sm font-medium">Transformation Type</label>
            <Select
              value={customTransformation.type || 'none'}
              onValueChange={(value) => setCustomTransformation({
                ...customTransformation,
                type: value === 'none' ? undefined : value as Transformation['type']
              })}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select transformation type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Select type...</SelectItem>

                {/* Basic Transformations */}
                <SelectItem value="resize">📐 Resize & Crop</SelectItem>
                <SelectItem value="format_conversion">🖼️ Format Conversion</SelectItem>
                <SelectItem value="color_adjustment">🌈 Color Adjustment</SelectItem>
                <SelectItem value="sharpen">🔍 Sharpen</SelectItem>
                <SelectItem value="blur">💫 Blur</SelectItem>

                {/* AI Features */}
                <SelectItem value="background_removal">🎨 AI Background Removal</SelectItem>
                <SelectItem value="ai_upscale">⚡ AI Upscaling (2x, 4x)</SelectItem>
                <SelectItem value="generative_remove">👁️ AI Generative Remove</SelectItem>
                <SelectItem value="generative_fill">🪄 AI Generative Fill</SelectItem>
                <SelectItem value="quality_enhancement">✨ AI Quality Enhancement</SelectItem>
                <SelectItem value="auto_enhancement">🔧 AI Auto Enhancement</SelectItem>
                <SelectItem value="noise_reduction">🔇 AI Noise Reduction</SelectItem>

                {/* Advanced AI Features */}
                <SelectItem value="generative_replace">🧠 AI Generative Replace</SelectItem>
                <SelectItem value="generative_background_replace">🌟 AI Generative Background Replace</SelectItem>
                <SelectItem value="auto_tagging">🏷️ AI Auto Tagging</SelectItem>
                <SelectItem value="object_detection">👁️‍🗨️ AI Object Detection</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Type-specific fields */}
          {customTransformation.type === 'background_removal' && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Background Color (optional)</label>
              <Select
                value={customTransformation.backgroundColor || 'transparent'}
                onValueChange={(value) => setCustomTransformation({
                  ...customTransformation,
                  backgroundColor: value
                })}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select background color" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="transparent">Transparent</SelectItem>
                  <SelectItem value="white">White</SelectItem>
                  <SelectItem value="black">Black</SelectItem>
                  <SelectItem value="lightgray">Light Gray</SelectItem>
                  <SelectItem value="lightblue">Light Blue</SelectItem>
                  <SelectItem value="lightgreen">Light Green</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {customTransformation.type === 'ai_upscale' && (
            <div className="space-y-2">
              <label className="text-sm font-medium">Scale Factor</label>
              <Select
                value={customTransformation.scale?.toString() || '2'}
                onValueChange={(value) => setCustomTransformation({
                  ...customTransformation,
                  scale: parseInt(value) as 2 | 4
                })}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select scale factor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2">2x Upscale - Double size</SelectItem>
                  <SelectItem value="4">4x Upscale - Quadruple size</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {customTransformation.type === 'resize' && (
            <>
              <div className="space-y-2">
                <label className="text-sm font-medium">Width (px)</label>
                <Input
                  type="number"
                  placeholder="Enter width"
                  value={customTransformation.width || ''}
                  onChange={(e) => setCustomTransformation({
                    ...customTransformation,
                    width: parseInt(e.target.value) || undefined
                  })}
                  max={4000}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Height (px)</label>
                <Input
                  type="number"
                  placeholder="Enter height"
                  value={customTransformation.height || ''}
                  onChange={(e) => setCustomTransformation({
                    ...customTransformation,
                    height: parseInt(e.target.value) || undefined
                  })}
                  max={4000}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Crop Mode</label>
                <Select
                  value={customTransformation.mode || 'pad'}
                  onValueChange={(value) => setCustomTransformation({
                    ...customTransformation,
                    mode: value as 'fit' | 'pad' | 'mfit' | 'fill' | 'scale'
                  })}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select crop mode" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pad">Pad - Exact dimensions with padding (recommended)</SelectItem>
                    <SelectItem value="fit">Fit - Contains entire image (may be smaller)</SelectItem>
                    <SelectItem value="fill">Fill - Fills dimensions, may crop</SelectItem>
                    <SelectItem value="crop">Crop - Extract region</SelectItem>
                    <SelectItem value="scale">Scale - Exact dimensions</SelectItem>
                    <SelectItem value="limit">Limit - Only scale down</SelectItem>
                    <SelectItem value="mfit">MFit - Scale up to fit</SelectItem>
                    <SelectItem value="thumb">Thumb - Smart thumbnail</SelectItem>
                    <SelectItem value="auto">Auto - AI determines best</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {customTransformation.type === 'format_conversion' && (
            <>
              <div className="space-y-2">
                <label className="text-sm font-medium">Format</label>
                <Select
                  value={customTransformation.format || 'none'}
                  onValueChange={(value) => setCustomTransformation({
                    ...customTransformation,
                    format: value === 'none' ? undefined : value as 'jpg' | 'png' | 'webp' | 'avif'
                  })}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select output format" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto - Best format</SelectItem>
                    <SelectItem value="jpg">JPG - Smaller files</SelectItem>
                    <SelectItem value="png">PNG - Transparency</SelectItem>
                    <SelectItem value="webp">WebP - Modern format</SelectItem>
                    <SelectItem value="avif">AVIF - Next-gen format</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Quality (%)</label>
                <Input
                  type="number"
                  placeholder="85"
                  value={customTransformation.quality || 85}
                  onChange={(e) => setCustomTransformation({
                    ...customTransformation,
                    quality: parseInt(e.target.value) || 85
                  })}
                  min={1}
                  max={100}
                />
              </div>
            </>
          )}

          {customTransformation.type === 'color_adjustment' && (
            <>
              <div className="space-y-2">
                <label className="text-sm font-medium">Brightness (-99 to 100)</label>
                <Input
                  type="number"
                  placeholder="0"
                  value={customTransformation.brightness || ''}
                  onChange={(e) => setCustomTransformation({
                    ...customTransformation,
                    brightness: e.target.value ? parseInt(e.target.value) : undefined
                  })}
                  min={-99}
                  max={100}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Saturation (-100 to 100)</label>
                <Input
                  type="number"
                  placeholder="0"
                  value={customTransformation.saturation || ''}
                  onChange={(e) => setCustomTransformation({
                    ...customTransformation,
                    saturation: e.target.value ? parseInt(e.target.value) : undefined
                  })}
                  min={-100}
                  max={100}
                />
              </div>
            </>
          )}
        </div>

        {/* Full width fields */}
        {customTransformation.type === 'generative_fill' && (
          <>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium">Target Width (px)</label>
                <Input
                  type="number"
                  placeholder="1200"
                  value={customTransformation.width || ''}
                  onChange={(e) => setCustomTransformation({
                    ...customTransformation,
                    width: parseInt(e.target.value) || undefined
                  })}
                  min={100}
                  max={4000}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Target Height (px)</label>
                <Input
                  type="number"
                  placeholder="1200"
                  value={customTransformation.height || ''}
                  onChange={(e) => setCustomTransformation({
                    ...customTransformation,
                    height: parseInt(e.target.value) || undefined
                  })}
                  min={100}
                  max={4000}
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Fill Style</label>
              <Select
                value={customTransformation.prompt || 'auto'}
                onValueChange={(value) => setCustomTransformation({
                  ...customTransformation,
                  prompt: value
                })}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select fill style" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto (Natural extension)</SelectItem>
                  <SelectItem value="product_showcase">Product Showcase (Clean studio)</SelectItem>
                  <SelectItem value="lifestyle">Lifestyle (Natural environment)</SelectItem>
                  <SelectItem value="minimal">Minimal (Simple background)</SelectItem>
                  <SelectItem value="seamless">Seamless Pattern</SelectItem>
                  <SelectItem value="gradient">Gradient Background</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        )}

        {customTransformation.type === 'generative_remove' && (
          <div className="space-y-2">
            <label className="text-sm font-medium">Objects to Remove</label>
            <Textarea
              placeholder="Describe what to remove (e.g., 'remove the background objects', 'remove the watermark')..."
              value={customTransformation.prompt || ''}
              onChange={(e) => setCustomTransformation({
                ...customTransformation,
                prompt: e.target.value
              })}
              maxLength={500}
              required
            />
          </div>
        )}

        {customTransformation.type === 'generative_replace' && (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">Replace This</label>
              <Input
                placeholder="Object to replace (e.g., 'the red shirt')"
                value={customTransformation.from || ''}
                onChange={(e) => setCustomTransformation({
                  ...customTransformation,
                  from: e.target.value
                })}
                maxLength={200}
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">With This</label>
              <Input
                placeholder="Replacement description (e.g., 'a blue shirt')"
                value={customTransformation.to || ''}
                onChange={(e) => setCustomTransformation({
                  ...customTransformation,
                  to: e.target.value
                })}
                maxLength={200}
                required
              />
            </div>
          </div>
        )}

        {customTransformation.type === 'generative_background_replace' && (
          <div className="space-y-2">
            <label className="text-sm font-medium">Background Description</label>
            <Textarea
              placeholder="Describe the new background (e.g., 'professional studio background', 'outdoor nature scene', 'modern office setting')..."
              value={customTransformation.prompt || ''}
              onChange={(e) => setCustomTransformation({
                ...customTransformation,
                prompt: e.target.value
              })}
              maxLength={500}
              required
            />
          </div>
        )}

        {customTransformation.type === 'auto_tagging' && (
          <div className="space-y-2">
            <label className="text-sm font-medium">Confidence Level</label>
            <Select
              value={customTransformation.confidence?.toString() || '0.7'}
              onValueChange={(value) => setCustomTransformation({
                ...customTransformation,
                confidence: parseFloat(value)
              })}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select confidence level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0.5">50% - More tags, less accuracy</SelectItem>
                <SelectItem value="0.7">70% - Balanced (recommended)</SelectItem>
                <SelectItem value="0.8">80% - Fewer tags, higher accuracy</SelectItem>
                <SelectItem value="0.9">90% - Very selective</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {customTransformation.type === 'object_detection' && (
          <div className="space-y-2">
            <label className="text-sm font-medium">Objects to Detect</label>
            <Input
              placeholder="Specific objects (e.g., 'person', 'car', 'product') or leave empty for all"
              value={customTransformation.objects || ''}
              onChange={(e) => setCustomTransformation({
                ...customTransformation,
                objects: e.target.value
              })}
            />
          </div>
        )}

        {/* Action buttons */}
        <div className="flex justify-end gap-3 pt-4">
          <Button 
            variant="outline" 
            onClick={() => {
              setShowCustomForm(false);
              setCustomTransformation({});
              setError(null);
            }}
          >
            Cancel
          </Button>
          <Button onClick={addCustomTransformation}>
            <CheckCircle className="w-4 h-4 mr-2" />
            Add Transformation
          </Button>
        </div>
      </div>
    );
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[90vw] max-h-[90vh] w-[90vw] h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0 pb-2">
          <DialogTitle className="flex items-center gap-2 text-xl">
            <FileImage className="w-5 h-5" />
            Process Product Images
          </DialogTitle>
          <DialogDescription className="text-base">
            Configure transformations for <strong>{product.name}</strong> ({imageCount} image{imageCount !== 1 ? 's' : ''})
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 flex flex-col space-y-4 min-h-0 overflow-y-auto" style={{ paddingBottom: '280px' }}>
          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Image Selection Section */}
          {(() => {
            const allImages = getAllImages();
            if (allImages.length > 1) {
              return (
                <Card className="flex-shrink-0">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex gap-2 items-center text-lg">
                        <Eye className="w-5 h-5" />
                        Select Images to Process
                      </CardTitle>
                      <div className="flex gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={selectAllImages}
                          disabled={selectedImages.length === allImages.length}
                        >
                          Select All
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={deselectAllImages}
                          disabled={selectedImages.length === 0}
                        >
                          Clear All
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Choose which images to apply transformations to ({selectedImages.length} of {allImages.length} selected)
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {allImages.map((imageUrl, index) => {
                        const isSelected = selectedImages.includes(imageUrl);
                        const metadata = getImageMetadata(imageUrl);
                        const types = metadata?.types || [];

                        return (
                          <div
                            key={index}
                            className={`relative group cursor-pointer rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                              isSelected
                                ? 'border-primary ring-2 ring-primary/20 shadow-md'
                                : 'border-gray-200 hover:border-primary/50'
                            }`}
                            onClick={() => toggleImageSelection(imageUrl)}
                          >
                            <div className="aspect-square bg-muted">
                              <img
                                src={imageUrl}
                                alt={`${product.name} - Image ${index + 1}`}
                                className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                              />
                            </div>

                            {/* Selection overlay */}
                            <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-200 ${
                              isSelected
                                ? 'bg-primary/20 opacity-100'
                                : 'bg-black/20 opacity-0 group-hover:opacity-100'
                            }`}>
                              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                                isSelected
                                  ? 'bg-primary border-primary'
                                  : 'bg-white border-white'
                              }`}>
                                {isSelected && (
                                  <CheckCircle className="w-4 h-4 text-white" />
                                )}
                              </div>
                            </div>

                            {/* Magento image roles/tags */}
                            {types.length > 0 && (
                              <div className="absolute top-2 left-2 right-2 flex flex-wrap gap-1">
                                {types.map((type: string, typeIndex: number) => (
                                  <Badge
                                    key={typeIndex}
                                    variant="secondary"
                                    className="text-[10px] px-1.5 py-0 h-5 bg-black/80 text-white border-none backdrop-blur-sm font-medium"
                                  >
                                    {formatImageRole(type)}
                                  </Badge>
                                ))}
                              </div>
                            )}

                            {/* Position badge */}
                            {metadata?.position !== undefined && (
                              <div className="absolute bottom-2 left-2 bg-black/80 text-white text-[10px] px-2 py-0.5 rounded backdrop-blur-sm font-medium">
                                Pos: {metadata.position}
                              </div>
                            )}

                            {/* Image number */}
                            <div className="absolute bottom-2 right-2 bg-black/80 text-white text-[10px] px-2 py-0.5 rounded backdrop-blur-sm font-medium">
                              #{index + 1}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              );
            }
            return null;
          })()}

          {/* Presets Section */}
          <Card className="flex-shrink-0">
            <CardHeader className="pb-3 pt-4">
              <CardTitle className="flex gap-2 items-center text-lg">
                <Sparkles className="w-5 h-5" />
                Quick Presets
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Available Presets */}
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {Object.entries(presets).map(([key, preset]) => (
                  <PresetCard
                    key={key}
                    presetKey={key}
                    preset={preset}
                    onClick={() => applyPreset(key)}
                  />
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Custom Transformations */}
          <Card className="flex-shrink-0">
            <CardHeader className="flex-shrink-0 pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex gap-2 items-center text-lg">
                  <Settings className="w-5 h-5" />
                  Custom Transformations
                </CardTitle>
                <div className="flex gap-2">
                  {transformations.length > 1 && !isOptimalOrder() && (
                    <Button variant="outline" size="sm" onClick={autoSortTransformations}>
                      <ArrowDown className="w-4 h-4 mr-1" />
                      Auto Sort
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => setShowCustomForm(true)}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add Custom
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Custom transformation form */}
              {renderCustomTransformationForm()}

              {/* Current transformations list */}
              {transformations.length > 0 && (
                <div className="space-y-4">
                  <h4 className="font-medium text-base flex items-center gap-2">
                    Applied Transformations
                    {transformations.length > 1 && !isOptimalOrder() && (
                      <Badge variant="outline" className="text-xs text-yellow-600 border-yellow-300">
                        Order can be optimized
                      </Badge>
                    )}
                  </h4>
                  <div className="space-y-3">
                    {transformations.map((transform, index) => (
                      <Card key={index} className="bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <span className="text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-full w-8 h-8 flex items-center justify-center font-medium">
                                {index + 1}
                              </span>
                              <span className="font-medium text-base text-gray-900 dark:text-gray-100">
                                {jobService.formatTransformation(transform)}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              {transformations.length > 1 && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => moveTransformation(index, Math.max(0, index - 1))}
                                    disabled={index === 0}
                                  >
                                    <ChevronUp className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => moveTransformation(index, Math.min(transformations.length - 1, index + 1))}
                                    disabled={index === transformations.length - 1}
                                  >
                                    <ChevronDown className="w-4 h-4" />
                                  </Button>
                                </>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeTransformation(index)}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {transformations.length === 0 && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <Settings className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="text-base font-medium mb-2">No transformations added yet</p>
                  <p className="text-sm">Select a preset above or click "Add Custom" to get started</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Processing Info & Actions - Absolutely positioned at bottom */}
        <div className="absolute bottom-0 left-0 right-0 bg-white dark:bg-gray-950 border-t dark:border-gray-800 shadow-lg z-10">
          {/* Processing Info */}
          <div className="p-4 border-b dark:border-gray-800">
            {renderProcessingEstimate()}
          </div>
          
          {/* Actions */}
          <div className="flex justify-end gap-3 p-6">
            <Button variant="outline" onClick={onClose} disabled={isCreatingJobs} size="lg">
              Cancel
            </Button>
            <Button 
              onClick={createJobs} 
              disabled={transformations.length === 0 || isCreatingJobs || creditCheckFailed || subscriptionLoading}
              className="min-w-[140px]"
              size="lg"
            >
              {isCreatingJobs ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : subscriptionLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : creditCheckFailed ? (
                <>
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Insufficient Credits
                </>
              ) : (
                <>
                  <FileImage className="w-4 h-4 mr-2" />
                  Process Images
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Preset Input Dialog */}
    {selectedPreset && (
      <PresetInputDialog
        preset={selectedPreset}
        isOpen={showPresetInputDialog}
        onClose={() => {
          setShowPresetInputDialog(false);
          setSelectedPreset(null);
        }}
        onConfirm={handlePresetInputConfirm}
      />
    )}
    </>
  );
}